import React, { useState, useEffect } from 'react';
import { Template, Organization } from '../../types';

interface DuplicateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string, description: string, organizationId?: number) => void;
  template: Template | null;
  organizations: Organization[];
  isLoading?: boolean;
}

const DuplicateTemplateModal: React.FC<DuplicateTemplateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  template,
  organizations,
  isLoading = false
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [organizationId, setOrganizationId] = useState<number | ''>('');
  const [errors, setErrors] = useState<{ name?: string; description?: string; organizationId?: string }>({});
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Initialize form with template data when modal opens
  useEffect(() => {
    if (template && isOpen) {
      setName(`${template.name}`);
      setDescription(template.description);
      setOrganizationId('');
      setErrors({});
    }
  }, [template, isOpen]);

  // Handle modal animation states
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger the fade-in animation after the component is rendered
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      // Wait for fade-out animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: { name?: string; description?: string; organizationId?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Worksheet name is required';
    } else if (name.trim().length < 3) {
      newErrors.name = 'Worksheet name must be at least 3 characters';
    }
    
    if (!description.trim()) {
      newErrors.description = 'Worksheet description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const selectedOrgId = organizationId === '' ? undefined : Number(organizationId);
      onConfirm(name.trim(), description.trim(), selectedOrgId);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName('');
      setDescription('');
      setOrganizationId('');
      setErrors({});
      onClose();
    }
  };

  if (!shouldRender) return null;

  return (
    <div className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300 z-50 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`}>
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-300 sm:my-8 sm:w-full sm:max-w-lg ${
            isVisible ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          }`}>
            <form onSubmit={handleSubmit}>
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <h3 className="text-xl font-bold leading-6 text-gray-900 mb-4 border-b pb-2">
                      Duplicate Worksheet
                    </h3>

                    <div className="space-y-4">
                      {/* Worksheet Name Field */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Worksheet Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
                            errors.name ? 'ring-red-300 focus:ring-red-500' : ''
                          }`}
                          placeholder="Enter worksheet name"
                          disabled={isLoading}
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                      </div>

                      {/* Worksheet Description Field */}
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                          Worksheet Description
                        </label>
                        <textarea
                          id="description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={3}
                          className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
                            errors.description ? 'ring-red-300 focus:ring-red-500' : ''
                          }`}
                          placeholder="Enter worksheet description"
                          disabled={isLoading}
                        />
                        {errors.description && (
                          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                        )}
                      </div>

                      {/* Organization Assignment Field */}
                      <div>
                        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                          Assign to Organization
                        </label>
                        <select
                          id="organization"
                          value={organizationId}
                          onChange={(e) => setOrganizationId(e.target.value === '' ? '' : Number(e.target.value))}
                          className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
                            errors.organizationId ? 'ring-red-300 focus:ring-red-500' : ''
                          }`}
                          disabled={isLoading}
                        >
                          <option value="">Select an organization (optional)</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                        {errors.organizationId && (
                          <p className="mt-1 text-sm text-red-600">{errors.organizationId}</p>
                        )}
                        {/* <p className="mt-1 text-xs text-gray-500">
                          If no organization is selected, the template will be available as a base template.
                        </p> */}
                      </div>
                    </div>
                    
                    {/* Informational Note */}
                    <div className="mt-6 mb-2 bg-blue-50 border border-blue-200 border-l-4 border-l-blue-400 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700">
                            <strong>Note:</strong> Duplicating creates a complete copy of this worksheet including all questions and steps. The original worksheet remains unchanged.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Duplicating...
                    </>
                  ) : (
                    'Duplicate Template'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateTemplateModal;